import { useRef, useEffect } from "react"

class ResizeObserverPolyfill {
    constructor(cb) {
        this.cb = cb
        this.elems = []
    }
    observe = elem => {
        if (this.elems.length === 0) {
            window.addEventListener("resize", this._refresh)
            if (typeof MutationObserver !== "undefined")
                this.mutationObserver = new MutationObserver(this._refresh)

            this.entries = {}
        }
        this.elems.push(elem)
        if (this.mutationObserver)
            this.mutationObserver.observe(elem, {
                attributes: true,
                childList: true,
                characterData: true,
                subtree: true,
            })
        if (!this._raf) this._raf = requestAnimationFrame(this._handleResize)
    }
    unobserve = elem => {
        if (this.mutationObserver) this.mutationObserver.disconnect()
        this.elems = this.elems.filter(el => el !== elem)
        if (this.elems.length === 0) {
            window.removeEventListener("resize", this._refresh)
            this.mutationObserver = null
            this._raf = null
        }
    }
    _handleResize = () => {
        if (!this._raf) return
        this._raf = null
        let entries = []
        let changed = false
        this.elems.forEach(target => {
            const rect = target.getBoundingClientRect()
            const contentRect = { width: rect.width, height: rect.height }
            if (
                !this.entries[target] ||
                this.entries[target].width !== contentRect.width ||
                this.entries[target].height !== contentRect.height
            ) {
                changed = true
                this.entries[target] = contentRect
            }
            entries.push({ target, contentRect })
        })
        if (changed) this.cb(entries)
    }

    _refresh = () => {
        if (!this._raf) this._raf = requestAnimationFrame(this._handleResize)
    }
}

const useRect = (target, cb) => {
    const ro = useRef()
    useEffect(() => {
        if (!ro.current) {
            const RObserver =
                typeof global.ResizeObserver !== "undefined"
                    ? global.ResizeObserver
                    : ResizeObserverPolyfill
            ro.current = new RObserver(entries => {
                if (Array.isArray(target)) {
                    cb(entries)
                    return
                }
                for (let e of entries) {
                    if (e.target === target.current) {
                        cb(e.contentRect)
                    }
                }
            })
        }
        const elems = Array.isArray(target) ? target.map(ref => ref.current) : target.current
        if (Array.isArray(elems)) elems.forEach(elem => ro.current.observe(elem))
        else ro.current.observe(elems)
        return () => {
            if (Array.isArray(elems)) elems.forEach(elem => ro.current.unobserve(elem))
            else ro.current.unobserve(elems)
        }
    }, [target, cb])
}
export default useRect
